import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import BrandMark from './BrandMark';
import AppIcon from './AppIcon';
import heroArtwork from '../assets/stockhunt-hero-v1.png';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { user, login } = useAuth();
  const navigate = useNavigate();

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(username, password);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message || 'Invalid username or password.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="auth-page">
      <div className="auth-shell">
        <aside className="auth-showcase">
          <button type="button" className="auth-brand" onClick={() => navigate('/')} aria-label="Back to StockHunt home">
            <span className="app-brand__mark"><BrandMark /></span>
            <span>StockHunt</span>
          </button>
          <div className="auth-showcase__copy">
            <span className="auth-eyebrow">Personal wealth intelligence</span>
            <h1>Welcome back to your financial command center.</h1>
            <p>Continue tracking your holdings, returns, market movement, and long-term wealth direction.</p>
            <div className="auth-benefits">
              <span><AppIcon name="check" /> One view across stocks, funds, and assets</span>
              <span><AppIcon name="check" /> Transaction-aware performance insights</span>
              <span><AppIcon name="check" /> Clear market and portfolio context</span>
            </div>
          </div>
          <img src={heroArtwork} alt="" className="auth-showcase__art" aria-hidden="true" />
        </aside>

        <section className="auth-form-panel" aria-labelledby="login-title">
          <div className="auth-form-card">
            <span className="auth-form-card__kicker">Secure account access</span>
            <h2 id="login-title">Sign in</h2>
            <p className="auth-form-card__intro">Enter your details to open your StockHunt workspace.</p>

            {error && <Alert variant="danger" className="auth-alert">{error}</Alert>}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="login-field-1">
                <Form.Label>Username</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter your username"
                  value={username}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => setUsername(event.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="username"
                />
              </Form.Group>

              <Form.Group className="mb-4" controlId="login-field-2">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  required
                  disabled={isLoading}
                  autoComplete="current-password"
                />
              </Form.Group>

              <Button variant="primary" type="submit" className="auth-submit" disabled={isLoading}>
                {isLoading ? <><Spinner animation="border" size="sm" /> Signing in…</> : 'Sign in to StockHunt'}
              </Button>
            </Form>

            <p className="auth-switch">
              New to StockHunt?{' '}
              <button type="button" onClick={() => navigate('/register')}>Create an account</button>
            </p>
          </div>
        </section>
      </div>
    </main>
  );
};

export default Login;
